import { Backdrop, CircularProgress } from "@mui/material";
import { useSnackbar } from "notistack";
import { useEffect, useState, VFC } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSignUp } from "src/hooks/useSignUp";
import { setHtmlLang } from "src/utils/setHtmlLang";
import Button from "../components/common/Button";
import InputField from "../components/common/InputField";

interface Props {
  lang?: string;
}

const SignUpPage: VFC<Props> = ({ lang = "en" }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [t, i18n] = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [errorMessage, setErrorMessage] = useState("");

  const code = searchParams.get("code");

  const { signUp, isLoading } = useSignUp({
    onSuccess: () => {
      enqueueSnackbar(t("sign_up.success"), { variant: "success" });
      navigate("/signin");
    },
    onError: () => {
      enqueueSnackbar(t("sign_up.fail"), { variant: "error" });
    },
  });

  useEffect(() => {
    if (lang) {
      i18n.changeLanguage(lang);
      setHtmlLang(lang);
    } else {
      i18n.changeLanguage("en");
      setHtmlLang("en");
    }
  }, [i18n, lang]);

  useEffect(() => {
    if (!code) {
      enqueueSnackbar(t("sign_up.invalid_code"), { variant: "error" });
      navigate("/signin");
    }
  }, [code, navigate, enqueueSnackbar, t]);

  useEffect(() => {
    if (password !== "" && password.length < 8) {
      setErrorMessage(t("sign_up.error.length"));
      return;
    }

    if (
      password !== "" &&
      confirmPassword !== "" &&
      password !== confirmPassword
    ) {
      setErrorMessage(t("sign_up.error.match"));
    } else {
      setErrorMessage("");
    }
  }, [password, confirmPassword, t]);

  const onChangePassword = (value: string) => {
    setPassword(value);
  };

  const onChangeConfirmPassword = (value: string) => {
    setConfirmPassword(value);
  };

  const handleSubmit = async () => {
    if (
      !code ||
      !password ||
      password !== confirmPassword ||
      password.length < 8
    ) {
      return;
    }

    await signUp({
      code,
      password,
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.nativeEvent.isComposing || e.key !== "Enter") return;
    handleSubmit();
  };

  const isSubmitDisabled =
    !code ||
    !password ||
    !confirmPassword ||
    password !== confirmPassword ||
    password.length < 8 ||
    isLoading;

  return (
    <main className="flex-grow flex justify-center items-center">
      <Backdrop open={isLoading} style={{ zIndex: 10001 }}>
        <CircularProgress />
      </Backdrop>
      <div className="shadow-md max-w-sp w-96 p-8 bg-white rounded-2xl">
        <div className="flex justify-center w-full text-2xl font-bold mb-2">
          {t("sign_up.title")}
        </div>
        <div className="text-center text-gray-600 mb-6">
          {t("sign_up.description")}
        </div>
        <div className="grid grid-cols-1 gap-4">
          <div>
            <InputField
              label={t("sign_up.password.label")}
              type="password"
              value={password}
              onChange={onChangePassword}
              placeholder={t("sign_up.password.placeholder")}
            />
          </div>
          <div>
            <InputField
              label={t("sign_up.confirm_password.label")}
              type="password"
              value={confirmPassword}
              onChange={onChangeConfirmPassword}
              placeholder={t("sign_up.confirm_password.placeholder")}
              onKeyDown={handleKeyDown}
            />
          </div>
          {errorMessage && (
            <div className="text-base text-red-400">{errorMessage}</div>
          )}
          <div>
            <Button
              onClick={handleSubmit}
              text={t("sign_up.button")}
              isFullWidth
              disabled={isSubmitDisabled}
            />
          </div>
        </div>
      </div>
    </main>
  );
};

export default SignUpPage;
